import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';

/**
 * Real-Time Global CO₂ Emissions Counter
 * 
 * Based on data from Global Carbon Project and Environmental Change Institute
 * 2024 global CO₂ emissions: ~37.4 billion tonnes/year (fossil fuels + industry)
 * Rate: ~1,186 tonnes per second
 * 
 * Reference epoch: Start of 2024 with cumulative emissions since 1750
 * Cumulative CO₂ from fossil fuels (1750-2023): ~1,740 billion tonnes
 */

// Emissions constants based on Global Carbon Project 2024 data
const ANNUAL_EMISSIONS_TONNES = 37.4e9; // 37.4 billion tonnes CO₂/year (2024 estimate)
const TONNES_PER_SECOND = ANNUAL_EMISSIONS_TONNES / (365.25 * 24 * 60 * 60); // ~1,186 tonnes/sec
const TONNES_PER_MS = TONNES_PER_SECOND / 1000;

// Reference point: Cumulative emissions at start of 2024 (in million tonnes)
// Historical cumulative: ~1,740 Gt CO₂ from fossil fuels (1750-2023)
const REFERENCE_DATE = new Date('2024-01-01T00:00:00Z');
const REFERENCE_EMISSIONS_MT = 1740000; // 1,740,000 million tonnes = 1,740 Gt

function formatNumber(num) {
  // Format with commas and 3 decimal places
  return num.toLocaleString('en-US', {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3
  });
}

// Individual digit component for odometer effect
function OdometerDigit({ digit, isDecimal = false }) {
  return (
    <span className="inline-block relative overflow-hidden h-[1em]">
      <motion.span
        key={digit}
        initial={{ y: '-100%' }}
        animate={{ y: 0 }}
        transition={{ 
          type: 'spring', 
          stiffness: 300, 
          damping: 30,
          mass: 0.8
        }}
        className="inline-block"
      >
        {digit}
      </motion.span>
    </span>
  );
}

export default function CO2EmissionsCounter() {
  const [emissions, setEmissions] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef(null);
  const animationRef = useRef(null);
  const lastUpdateRef = useRef(Date.now());

  // Calculate current emissions based on time elapsed since reference
  const calculateEmissions = () => {
    const now = Date.now();
    const msElapsed = now - REFERENCE_DATE.getTime();
    const additionalEmissionsTonnes = msElapsed * TONNES_PER_MS;
    const totalEmissionsMT = REFERENCE_EMISSIONS_MT + (additionalEmissionsTonnes / 1000000);
    return totalEmissionsMT;
  };

  // Intersection observer for visibility
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { threshold: 0.1 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Animation loop - updates every 50ms for smooth odometer effect
  useEffect(() => {
    if (!isVisible) {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      return;
    }

    const updateCounter = () => {
      const now = Date.now();
      // Only update every 50ms for performance
      if (now - lastUpdateRef.current >= 50) {
        setEmissions(calculateEmissions());
        lastUpdateRef.current = now;
      }
      animationRef.current = requestAnimationFrame(updateCounter);
    };

    // Initial calculation
    setEmissions(calculateEmissions());
    animationRef.current = requestAnimationFrame(updateCounter);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isVisible]);

  const formattedEmissions = formatNumber(emissions);

  // Calculate emissions rate for display
  const tonnesPerSecond = Math.round(TONNES_PER_SECOND).toLocaleString();

  return (
    <section 
      ref={containerRef}
      className="relative py-16 bg-gray-950 overflow-hidden"
    >
      {/* Subtle animated background */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-gray-800 to-transparent animate-pulse" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          {/* Label */}
          <div className="mb-4">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-red-900/30 border border-red-800/50 rounded-full text-red-400 text-sm font-medium">
              <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
              Live Global Data
            </span>
          </div>

          <h2 className="text-xl md:text-2xl font-medium text-gray-400 mb-6">
            Total CO<sub>2</sub> Emissions from Fossil Fuels
            <span className="block text-sm text-gray-500 mt-1">(Million Tonnes since 1750)</span>
          </h2>

          {/* Main Counter */}
          <div className="relative">
            <div 
              className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold text-white tracking-tight font-mono"
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {formattedEmissions}
            </div>
            
            {/* Glow effect */}
            <div className="absolute inset-0 blur-3xl opacity-20 bg-gradient-to-r from-red-600 via-orange-500 to-red-600 -z-10" />
          </div>

          {/* Rate indicator */}
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-4 text-gray-400">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
              <span className="text-sm">
                <span className="text-red-400 font-semibold">{tonnesPerSecond}</span> tonnes/second
              </span>
            </div>
            <span className="hidden sm:inline text-gray-600">•</span>
            <div className="text-sm">
              <span className="text-orange-400 font-semibold">~2.4 million</span> tonnes/hour
            </div>
          </div>

          {/* Source attribution */}
          <p className="mt-6 text-xs text-gray-500">
            Source:{' '}
            <a 
              href="https://www.globalcarbonproject.org/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-white underline underline-offset-2"
            >
              Global Carbon Project
            </a>
            {' & '}
            <a 
              href="https://www.globalwarmingindex.org/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-white underline underline-offset-2"
            >
              Environmental Change Institute
            </a>
            , University of Oxford
          </p>

          {/* Call to action - narrative bridge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="mt-10 max-w-2xl mx-auto"
          >
            <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 border border-gray-700 rounded-2xl p-6">
              <p className="text-gray-300 text-lg leading-relaxed">
                Every second, we add <span className="text-red-400 font-semibold">1,186 tonnes</span> of CO₂ to our atmosphere. 
                While we transition to renewables, we need <span className="text-primary-400 font-semibold">cleaner gas turbines</span> to 
                bridge the gap—cutting emissions by up to <span className="text-primary-400 font-semibold">50%</span> with hydrogen blending.
              </p>
              <div className="mt-4 flex justify-center">
                <a 
                  href="/interactive-tools" 
                  className="inline-flex items-center gap-2 text-primary-400 hover:text-primary-300 font-medium transition-colors"
                >
                  See how we're helping
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </a>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
